/* eslint-disable max-statements */
/* eslint-disable i18n-scan/no-inline-content */

import { useMutation } from '@apollo/client'
import {
  BodyText,
  Button,
  CaptionText,
  HeadingText,
} from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { isEmpty, isNil, merge, size } from 'lodash-es'
import SpinnerComponent from 'ppbl/components/SpinnerAdapter'
import {
  acceptSelectedOffer,
  createSelectedOffer,
} from 'ppbl/graphql/offers/mutations'
import { useApplicationContext, useExperiments } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import serverData from 'ppbl/utils/server-data'
import { interpolate, santizeInputFields } from 'ppbl/utils/string-helper'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { financialCalculatorService } from '../../services/financialCalculator.service'
import {
  AverageMonthlyGrossSales,
  Offer,
  OfferConstraints,
  ProductOfferConstraint,
} from '../../types'
import {
  currencyParser,
  getDisclosureStatus,
  getRenewalStatus,
  isRenewalFlow,
  getPortalUrl,
  scrollToTopOnContinue,
  handleGraphqlError,
} from '../__utils__'
import DisclosureModal from './disclosureModal'
import { OfferContext } from './offerContext'
import OfferHeader from './offerHeader'
import OfferInfo from './offerInfo'
import OfferInfoV2 from './offerInfoV2'
import OfferInput from './offerInput'
import OfferInputV2 from './offerInputV2'
import './styles.css'
import TermsInput from './termInput'
import { CaseTypes } from 'ppbl/utils/constants'

type footNoteProps = {
  footerNoteArray: string[]
}

const FootNote: React.FC<footNoteProps> = ({ footerNoteArray }) => {
  return (
    <div className="footerNoteSection">
      {footerNoteArray.map((footerNote, index) => {
        return (
          <div className="footerNote" key={`footNote-${index}`}>
            <CaptionText
              className="footerText"
              style={{ marginRight: '0.25rem' }}
            >
              <sup>{index + 1}</sup>
            </CaptionText>
            <CaptionText className="footerText">{footerNote}</CaptionText>
          </div>
        )
      })}
    </div>
  )
}

type offerModelProps = {
  offerViewVersion: boolean
  showTermInput: boolean
}

const OfferModel: React.FC<offerModelProps> = ({
  offerViewVersion,
  showTermInput,
}) => {
  return offerViewVersion ? (
    <>
      <HeadingText size="title">
        {getLocalizedText('offer-page.offerConfigurator.headingText1Opt1')}
      </HeadingText>
      <div className="offerSectionV2">
        <OfferInputV2 />
      </div>
      <HeadingText size="title">
        {getLocalizedText('offer-page.offerConfigurator.headingText2Opt1')}
      </HeadingText>
      <OfferInfoV2 />
    </>
  ) : (
    <>
      <HeadingText size="title">
        {getLocalizedText('offer-page.offerConfigurator.headingText1Opt2')}
      </HeadingText>
      <div className="offerSection">
        <OfferInput />
      </div>
      {showTermInput ? (
        <>
          <HeadingText size="title">
            {getLocalizedText('offer-page.offerConfigurator.headingText2Opt2')}
          </HeadingText>
          <div className="offerSection">
            <TermsInput />
          </div>
        </>
      ) : (
        <></>
      )}
      <OfferInfo />
    </>
  )
}

type Props = {
  applicationId: string
  averageMonthlyGrossSales: AverageMonthlyGrossSales
  offerConstraints: OfferConstraints
  customOfferConstraints: Offer[]
  productOfferConstraints: ProductOfferConstraint[]
  applicationType: string
}

/**
 * This is a functional component that displays offer configurator
 * it is composed of several small comonents such as input, term and infometrics
 * it is responsible for selection of loan & term and then creation and selection of offer through GQL APIs
 * @returns {JSX.Element} - A JSX element
 */

const OfferConfigurator: React.FC<Props> = ({
  offerConstraints,
  averageMonthlyGrossSales,
  applicationId,
  customOfferConstraints,
  productOfferConstraints,
  applicationType,
}) => {
  let offerMaxAmount = Number(offerConstraints[0].maxLoanAmount.value)
  const offerTermOptions =
    serverData.data.appConfig.application.offerTermOptions
  const {
    minAmount: { value: minAmount },
  } = productOfferConstraints[0]
  let offerMinAmount = Number(minAmount)

  const { ppbl_offer_page = 'control' } = useExperiments()

  const disclosureLoadRef = useRef(0)

  const {
    data: {
      applicationMetaData: { businessRegion, portalAccess, legalBusinessName },
      offerMetaData: {
        loanHistoryData: {
          formattedTotalOutstandingPrincipalBalance,
          previousLoans,
        },
      },
    },
  } = useApplicationContext()

  const renewalStatus = getRenewalStatus(applicationType)
  const showDisclosure = getDisclosureStatus(businessRegion)

  const renewalToken = serverData.startParams?.renewalToken

  const previousLoanOutstandingAmount = currencyParser(
    formattedTotalOutstandingPrincipalBalance.amount,
  )

  let footNoteArray = [
    getLocalizedText('offer-page.offerConfigurator.footerNote1'),
  ]

  if (renewalStatus === 'LOAN_RENEWAL' && !isEmpty(previousLoans)) {
    footNoteArray.push(
      getLocalizedText('offer-page.offerConfigurator.footerNote2'),
    )
  }

  const [loanTerm, setLoanTerm] = useState(52)
  const [weeklyPayment, setWeeklyPayment] = useState(0)
  const [loanFee, setLoanFee] = useState(0)
  const [loanPayOff, setLoanPayoff] = useState(0)
  const [annualPercentageRate, setAnnualPercentageRate] = useState(0)
  const [averageMonthlyCost, setAverageMonthlyCost] = useState(0)
  const [disbursementAmount, setDisbursementAmount] = useState('')
  const [termInDays, setTermInDays] = useState(0)
  const [loanAmount, updateLoanAmount] = useState(String(offerMaxAmount))
  const [offerValues, updateOfferValues] = useState<any>({})
  const [continueDisabled, disableContinue] = useState(false)

  // Offer Options for user to select
  let offerOptions = offerTermOptions.map((each) => {
    return {
      label: interpolate(
        getLocalizedText('offer-page.offerConfigurator.offerOptionsLabel'),
        {
          offerTerm: each,
        },
      ),
      value: each,
      maxInput: financialCalculatorService.getOfferByTerm(
        customOfferConstraints,
        Number(each),
      )?.maxAmount?.value,
    }
  })

  //filter options by term
  offerOptions = offerOptions.filter((option) => {
    return option.maxInput
  })

  const showTermInput = size(offerOptions) > 1
  const isV2 = ppbl_offer_page === 'treatment_1' && showTermInput ? true : false

  const fptiData = {
    page: `offerPage_${ppbl_offer_page}_${renewalStatus}`,
  }

  useEffect(() => {
    trackPage(
      merge(fptiData, {
        applicationId,
      }),
    )
    sessionStorage.setItem('mostRecentCompletedPage', 'offer')
    clientLogger.info('Offer Page is loaded - Application ID:', applicationId)
  }, [])

  const [acceptOffer, { loading: acceptOfferLoading }] = useMutation(
    acceptSelectedOffer,
    {
      onCompleted(data) {
        clientLogger.info(
          'Accept Offer Call successful - Response Received',
          data,
        )
        if (!showDisclosure && !isNil(data)) {
          if (isRenewalFlow(applicationType)) {
            const portalUrl = getPortalUrl(
              portalAccess,
              CaseTypes.PENDING_STIPULATIONS,
            )
            window.open(portalUrl, '_self')
          } else {
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/thank-you`,
            )
          }
        }
      },
      onError(acceptOfferError) {
        handleGraphqlError(
          acceptOfferError,
          'Error encountered while accepting selected offer in offer page',
          'OfferPage - createSelectedOffer',
          applicationId,
          renewalToken,
          legalBusinessName,
          applicationType,
        )
      },
    },
  )

  // this create offer based on selected term and offer amount
  const [createOffer, { loading: createOfferLoading }] = useMutation(
    createSelectedOffer,
    {
      onCompleted(createdOffer) {
        clientLogger.info(
          'Create Offer Call successful - Response Received',
          createOffer,
        )
        const {
          credit: {
            businessCredit: {
              createOffer: {
                offer: {
                  id: createdOfferId,
                  formattedAnnualPercentageRate: {
                    percentage: newAnnualPercentageRate,
                  },
                  formattedAmount: { amount: newDisbursementAmount },
                  formattedAverageMonthlyCost: {
                    amount: newAverageMonthlyCost,
                  },
                  formattedTerm: { formattedTimeDuration: newTermInDays },
                },
              },
            },
          },
        } = createdOffer
        setAnnualPercentageRate(newAnnualPercentageRate)
        setDisbursementAmount(newDisbursementAmount)
        setAverageMonthlyCost(newAverageMonthlyCost)
        setTermInDays(newTermInDays)
        if (!isNil(applicationId) && !isNil(createdOfferId)) {
          acceptOffer({
            variables: {
              offerInput: {
                applicationId,
                offerId: createdOfferId,
              },
            },
          })
        }
      },
      onError(createOfferError) {
        handleGraphqlError(
          createOfferError,
          'Error encountered while creating selected offer in offer page',
          'OfferPage - createSelectedOffer',
          applicationId,
          renewalToken,
          legalBusinessName,
          applicationType,
        )
      },
    },
  )

  /**
   * Calculates various financial values based on input parameters.
   * @param {number} maximumLoanAmount - The maximum loan amount.
   * @param {number} termInWeeks - The term of the loan in weeks.
   * @param {number} desiredAmount - The desired loan amount.
   * @returns {object} - Calculated financial values.
   */
  const calculateOfferValues = (
    maximumLoanAmount,
    termInWeeks,
    desiredAmount,
  ) => {
    if (!maximumLoanAmount) {
      clientLogger.error(
        `maxAmount is undefined or null for applicationID: ${applicationId}`,
      )
      return {}
    }
    if (!termInWeeks) {
      clientLogger.error(
        `term is undefined or null for applicationID: ${applicationId}`,
      )
      return {}
    }
    if (!desiredAmount) {
      clientLogger.warn(
        `desiredAmount is undefined or null for applicationID: ${applicationId}`,
      )
      return {}
    }
    const averageGrossScore = averageMonthlyGrossSales.value || null
    const offers = customOfferConstraints
    const values: any = {}
    values.offerAmount = desiredAmount
    values.termInWeeks = termInWeeks
    values.monthlyRevenue = Number(averageGrossScore)
    values.weeklyRevenue = financialCalculatorService.getWeeklyRevenue(
      values.monthlyRevenue,
    )
    values.weeklyRate = financialCalculatorService.getWeeklyPriceByOffer(
      termInWeeks,
      offers,
    )
    values.weeklyPayment = financialCalculatorService.getWeeklyTotalPayment(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )
    values.weeklyPrincipalPayment =
      financialCalculatorService.getWeeklyPrincipalPayment(
        values.offerAmount,
        values.termInWeeks,
      )

    values.weeklyInterestPayment =
      financialCalculatorService.getWeeklyInterestPayment(
        values.offerAmount,
        values.weeklyRate,
      )

    values.centsOnTheDollar = financialCalculatorService.getCentsOnTheDollar(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )

    values.financeCharges = financialCalculatorService.getTotalFinanceCharges(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )
    values.totalRepayment = financialCalculatorService.getTotalAmountOfPayments(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )

    values.annualInterestRate =
      financialCalculatorService.getAnnualInterestRate(
        offers,
        values.weeklyRate,
        values.termInWeeks,
      )

    values.totalInterestPercentage =
      financialCalculatorService.getTotalInterestPercent(
        values.termInWeeks,
        values.weeklyRate,
      )

    values.totalInterestCharge =
      financialCalculatorService.getTotalInterestCharge(
        values.termInWeeks,
        values.weeklyInterestPayment,
      )

    values.termInMonths = financialCalculatorService.getTermInMonths(
      values.termInWeeks,
    )
    values.maxProportionOfRevenue =
      financialCalculatorService.getMaxProportionOfRevenue(
        values.offerAmount,
        values.termInWeeks,
        values.weeklyRevenue,
      )

    values.isQualified = financialCalculatorService.isQualified(
      values.offerAmount,
      values.termInWeeks,
      values.maxProportionOfRevenue,
      maximumLoanAmount,
      offers,
    )

    values.frsaSplitPercentage =
      financialCalculatorService.getFRSASplitPercentage(
        values.totalRepayment,
        values.weeklyRevenue,
        values.termInWeeks,
      )
    values.annualPercentageRate =
      financialCalculatorService.getAnnualPercentageRate(
        offers,
        values.weeklyRate,
        values.termInWeeks,
      )
    values.purchaseAmount = financialCalculatorService.getPurchaseAmount(
      values.offerAmount,
      values.totalInterestCharge,
    )

    values.dailyCollectionAmount =
      financialCalculatorService.getDailyCollectionAmount(
        values.purchaseAmount,
        values.termInMonths,
      )

    values.dailyCollectionProportionOfRevenue =
      financialCalculatorService.getDailyCollectionProportionOfRevenue(
        values.dailyCollectionAmount,
        values.monthlyRevenue,
      )

    values.weeklyCollectionAmount =
      financialCalculatorService.getWeeklyCollectionAmount(
        values.purchaseAmount,
        values.termInWeeks,
      )

    values.weeklyCollectionProportionOfRevenue =
      financialCalculatorService.getWeeklyCollectionProportionOfRevenue(
        values.weeklyCollectionAmount,
        values.monthlyRevenue,
      )

    try {
      const offer = financialCalculatorService.getOfferByTerm(
        offers,
        values.termInWeeks,
      )

      if (!offer) {
        clientLogger.error(
          `No offer found for termInWeeks ${values.termInWeeks}`,
        )
        return values
      }

      values.maxAutomatedOfferAmount = offer.maxAutomatedOfferAmount
      values.maxEstimatedVerifiedAutomatedOfferAmount =
        offer.maxEstimatedVerifiedAutomatedOfferAmount
      values.weeklyInterestRate = offer.weeklyInterestRate
    } catch (err) {
      clientLogger.error(`Error on calculateValues: ${err.message}`, err)
    }
    return values
  }

  // memoize the calculate offer value function
  const calculateOfferMetrics = useCallback(calculateOfferValues, [
    applicationId,
    customOfferConstraints,
    averageMonthlyGrossSales.value,
  ])

  // if loan amount or term or max amount changes, recalculate
  useEffect(() => {
    let offerInfoValue = calculateOfferMetrics(
      Number(offerMaxAmount),
      Number(loanTerm),
      Number(loanAmount),
    )
    updateOfferValues(offerInfoValue)
    disableContinue(continueDisabled)
  }, [offerMaxAmount, loanTerm, loanAmount, calculateOfferMetrics])

  // Disclosure Modal state
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  /**
   * Triggers GQL Api for create offer, if create offer is successfull, subsecquently accept offer is called
   */
  const handleOfferSubmit = async () => {
    if (!continueDisabled) {
      const term =
        offerValues?.termInWeeks !== undefined
          ? `P${offerValues?.termInWeeks}W`
          : null
      const proportionOfBusinessRevenue = offerValues?.maxProportionOfRevenue
      const weeklyInterestRate = offerValues?.weeklyInterestRate
      const offerAmount = String(offerValues.offerAmount)
      santizeInputFields(offerAmount)
      const fee = String(offerValues.financeCharges)
      if (!showDisclosure) {
        trackClick(
          merge(fptiData, {
            component: `offerAccept_${ppbl_offer_page}_continue_button`,
            link: `offerAccept_${ppbl_offer_page}_continue_button`,
            applicationId,
          }),
        )
        scrollToTopOnContinue()
      }

      createOffer({
        variables: {
          offerInput: {
            applicationId,
            offer: {
              term,
              proportionOfBusinessRevenue,
              weeklyInterestRate,
              amount: { currencyCode: 'USD', value: offerAmount },
              fee: { currencyCode: 'USD', value: fee },
            },
          },
        },
      })
    }
  }
  return (
    <div>
      <OfferContext.Provider
        value={{
          loanAmount,
          loanTerm,
          offerOptions,
          weeklyPayment,
          annualPercentageRate,
          customOfferConstraints,
          averageMonthlyCost,
          disbursementAmount,
          termInDays,
          loanFee,
          loanPayOff,
          offerMaxAmount,
          renewalStatus,
          offerMinAmount,
          offerValues,
          previousLoans,
          previousLoanOutstandingAmount,
          updateLoanAmount,
          setLoanTerm,
          setWeeklyPayment,
          setLoanFee,
          setLoanPayoff,
          continueDisabled,
          disableContinue,
          fptiData,
          applicationId,
        }}
      >
        {!showDisclosure && (createOfferLoading || acceptOfferLoading) ? (
          <div id="spinnerContainer-configurator" className="spinner">
            <SpinnerComponent />
          </div>
        ) : (
          <>
            <div className="offerSection">
              <OfferHeader />
            </div>

            <OfferModel offerViewVersion={isV2} showTermInput={showTermInput} />
            <BodyText className="helperText">
              {getLocalizedText('offer-page.offerConfigurator.footerText')}
            </BodyText>
            <div className="center">
              {showDisclosure ? (
                <DisclosureModal
                  applicationType={applicationType}
                  buttonClassName="continueButton button"
                  applicationId={applicationId}
                  buttonDisabled={continueDisabled}
                  onClick={handleOfferSubmit}
                  createOfferLoading={createOfferLoading}
                  acceptOfferLoading={acceptOfferLoading}
                  isSheetOpen={isSheetOpen}
                  setIsSheetOpen={setIsSheetOpen}
                  portalAccess={portalAccess}
                  loadRef={disclosureLoadRef}
                />
              ) : (
                <Button
                  className="continueButton button"
                  size="lg"
                  onClick={handleOfferSubmit}
                  disabled={continueDisabled}
                >
                  {getLocalizedText('offer-page.offerConfigurator.continueBtn')}
                </Button>
              )}
            </div>
            <FootNote footerNoteArray={footNoteArray} />
          </>
        )}
      </OfferContext.Provider>
    </div>
  )
}

export default OfferConfigurator
